<main id="main-content" class="ds-product-page">
  <section class="intro">
    <p class="eyebrow">{{ 'company.eyebrow' | t }}</p>
    <h1>{{ 'company.title' | t }}</h1>
    <p>{{ 'company.lede' | t }}</p>
  </section>

  @if (error()) {
    <p class="error" role="alert">{{ error() }}</p>
  }

  @if (!user()) {
    <form (ngSubmit)="login()" aria-labelledby="login-heading">
      <h2 id="login-heading">{{ 'common.developmentLogin' | t }}</h2>
      <label for="email">{{ 'common.email' | t }}</label>
      <input
        dsInput
        id="email"
        type="email"
        autocomplete="email"
        [formControl]="email"
        [attr.aria-invalid]="email.invalid && email.touched"
        [attr.aria-describedby]="email.invalid && email.touched ? 'company-email-error' : null"
      />
      @if (email.invalid && email.touched) {
        <p id="company-email-error" class="field-error" role="alert">
          {{ 'company.validEmail' | t }}
        </p>
      }
      <button dsButton type="submit" [disabled]="isBusy()">{{ 'common.continue' | t }}</button>
    </form>
  } @else {
    <section class="workspace" aria-labelledby="organizations-heading">
      <div class="signed-in">
        <span>{{ 'company.signedIn' | t }}</span>
        <strong>{{ user()?.email }}</strong>
      </div>

      <form (ngSubmit)="createOrganization()">
        <h2 id="organizations-heading">{{ 'company.organizations' | t }}</h2>
        <label for="organization-name">{{ 'company.newOrganization' | t }}</label>
        <div class="inline-field">
          <input
            dsInput
            id="organization-name"
            [formControl]="organizationName"
            maxlength="200"
            [attr.aria-invalid]="organizationName.invalid && organizationName.touched"
            [attr.aria-describedby]="
              organizationName.invalid && organizationName.touched
                ? 'organization-name-error'
                : null
            "
          />
          <button dsButton type="submit" [disabled]="isBusy()">{{ 'company.create' | t }}</button>
        </div>
        @if (organizationName.invalid && organizationName.touched) {
          <p id="organization-name-error" class="field-error" role="alert">
            {{ 'company.organizationRequired' | t }}
          </p>
        }
      </form>

      @if (organizations().length === 0) {
        <p class="empty">{{ 'company.none' | t }}</p>
      } @else {
        <ul>
          @for (organization of organizations(); track organization.id) {
            <li>
              <div>
                <strong>{{ organization.name }}</strong>
                <span>{{ organization.slug }}</span>
              </div>
              <span class="status">{{ 'common.active' | t }}</span>
              <button
                dsButton
                type="button"
                class="small"
                (click)="selectOrganization(organization)"
              >
                {{ 'company.manage' | t }}
              </button>
            </li>
          }
        </ul>
      }

      @if (selectedOrganization(); as organization) {
        <section class="campaign-workspace" aria-labelledby="campaign-heading">
          @if (dashboard(); as metrics) {
            <section aria-labelledby="dashboard-heading">
              <h2 id="dashboard-heading">{{ 'company.dashboard' | t }}</h2>
              <div class="metrics">
                <p>
                  <strong>{{ metrics.availableCredits }}</strong
                  ><span>{{ 'company.availableCredits' | t }}</span>
                </p>
                <p>
                  <strong>{{ metrics.reservedCredits }}</strong
                  ><span>{{ 'company.reservedCredits' | t }}</span>
                </p>
                <p>
                  <strong>{{ metrics.consumedCredits }}</strong
                  ><span>{{ 'company.consumedCredits' | t }}</span>
                </p>
                <p>
                  <strong>{{ metrics.invitationsIssued }}</strong
                  ><span>{{ 'company.invitations' | t }}</span>
                </p>
                <p>
                  <strong>{{ metrics.completedAssessments }}</strong
                  ><span>{{ 'company.completed' | t }}</span>
                </p>
                <p>
                  <strong>{{ metrics.completionRate * 100 }}%</strong
                  ><span>{{ 'company.completionRate' | t }}</span>
                </p>
              </div>
            </section>
          }

          <section aria-labelledby="billing-heading">
            <h2 id="billing-heading">{{ 'company.billing' | t }}</h2>
            <p class="development-note">
              {{ 'company.simulated' | t }}
            </p>
            <div class="bundles">
              @for (bundle of creditBundles(); track bundle.id) {
                <article>
                  <strong>{{ bundle.name }}</strong>
                  <span>{{ bundle.priceMinorUnits / 100 }} {{ bundle.currency }}</span>
                  <button
                    dsButton
                    type="button"
                    class="small"
                    (click)="purchaseBundle(bundle)"
                    [disabled]="isBusy()"
                  >
                    {{ 'company.purchase' | t }}
                  </button>
                </article>
              }
            </div>
            @if (invoices().length) {
              <ul aria-label="Invoices">
                @for (invoice of invoices(); track invoice.id) {
                  <li>
                    <div>
                      <strong>{{ invoice.invoiceNumber }}</strong
                      ><span
                        >{{ invoice.credits }} {{ 'common.credits' | t }} ·
                        {{ invoice.status }}</span
                      >
                    </div>
                    @if (invoice.status === 'Paid') {
                      <button
                        dsButton
                        type="button"
                        class="small"
                        (click)="refundInvoice(invoice)"
                        [disabled]="isBusy()"
                      >
                        {{ 'company.refund' | t }}
                      </button>
                    }
                  </li>
                }
              </ul>
            }
          </section>

          <h2 id="campaign-heading">{{ 'company.campaignsFor' | t }} {{ organization.name }}</h2>
          <p>
            <strong>{{ availableCredits() }}</strong> {{ 'company.assessmentCredits' | t }}
          </p>
          <button dsButton type="button" (click)="grantDevelopmentCredits()" [disabled]="isBusy()">
            {{ 'company.addCredits' | t }}
          </button>

          <form (ngSubmit)="createCampaign()">
            <label for="campaign-name">{{ 'company.newCampaign' | t }}</label>
            <div class="inline-field">
              <input dsInput id="campaign-name" [formControl]="campaignName" maxlength="200" />
              <button dsButton type="submit" [disabled]="isBusy() || assessments().length === 0">
                {{ 'company.createActivate' | t }}
              </button>
            </div>
          </form>

          @if (campaigns().length === 0) {
            <p class="empty">{{ 'company.noCampaigns' | t }}</p>
          } @else {
            <ul>
              @for (campaign of campaigns(); track campaign.id) {
                <li>
                  <div>
                    <strong>{{ campaign.name }}</strong
                    ><span>{{ campaign.status }}</span>
                  </div>
                  <button
                    dsButton
                    type="button"
                    class="small"
                    (click)="issueInvitation(campaign)"
                    [disabled]="isBusy() || availableCredits() < 1"
                  >
                    {{ 'company.issue' | t }}
                  </button>
                </li>
              }
            </ul>
          }

          @if (issuedInvitation(); as invitation) {
            <div class="invitation" role="status">
              <strong>{{ 'company.invitationCreated' | t }}</strong>
              <code>{{ invitation.secret }}</code>
              <a href="/candidate?token={{ invitation.secret }}">{{
                'company.openCandidate' | t
              }}</a>
            </div>
          }
        </section>
      }
    </section>
  }
</main>
